import { userListQuerySchema, type Page, type UserListQuery } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { toListParams } from '../../../shared/policies/list-params';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { listUsers, type UserListItem } from '../users.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type UsersState =
  { status: 'loading' } | { status: 'loaded'; page: Page<UserListItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: UserListQuery) => toListParams(query).toString();

function toState(page: Page<UserListItem>, query: UserListQuery): UsersState {
  return { status: 'loaded', page, queryKey: keyOf(query) };
}

function toErrorState(err: unknown): UsersState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of users the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes. */
export function useUserList() {
  const list = useListQuery(userListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<UsersState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    listUsers(query)
      .then((page) => {
        if (!cancelled) setState(toState(page, query));
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState(toState(await listUsers(query), query));
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing, reload };
}
